import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { 
  FeatherX,
  FeatherEdit2,
  FeatherTrash2,
  FeatherGlobe,
  FeatherMail,
  FeatherPhone,
  FeatherUser,
  FeatherCalendar,
  FeatherTag,
  FeatherMessageSquare,
  FeatherActivity,
  FeatherBell,
  FeatherDownload,
  FeatherUpload,
  FeatherCheckCircle,
  FeatherClock,
  FeatherFileText,
  FeatherUsers,
  FeatherPlus,
  FeatherAlertCircle,
  FeatherTrendingUp,
  FeatherFlag,
  FeatherRefreshCw,
  FeatherArrowLeft
} from '../ui/icons';
import { IconButton } from '../ui/IconButton';
import CommunicationLogs from './CommunicationLogs';
import ActivityFeed from './ActivityFeed';
import Reminders from './Reminders';
import ContactHistory from './ContactHistory';
import { usePartnerStatusCompat } from '../../context/PartnerStatusContext';
import api from '../../services/api';
import './CompanyDetailView.css';

const CompanyDetailView = ({ company, onClose, onEdit, onDelete, onStatusUpdate, users }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyData, setCompanyData] = useState(company);
  
  // Use the partner status context instead of hardcoded functions
  const statusManager = usePartnerStatusCompat();

  useEffect(() => {
    if (company?.id) {
      fetchCompanyDetails();
    }
  }, [company?.id]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/partner-companies/${company.id}`);
      setCompanyData(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch company details');
      console.error('Error fetching company details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.patch(`/api/partner-companies/${company.id}/status`, {
        status: newStatus
      });
      
      setCompanyData(prev => ({
        ...prev,
        partner_status: { current_status: newStatus }
      }));
      
      if (onStatusUpdate) {
        onStatusUpdate(company.id, newStatus);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  // Use the status manager's formatStatus instead of hardcoded logic
  const formatStatus = (status) => {
    return statusManager.formatStatus(status);
  };

  // Use the status manager's getAvailableStatusOptions instead of hardcoded logic
  const getAvailableStatusOptions = (currentStatus) => {
    return statusManager.getAvailableStatusOptions(currentStatus);
  };

  // Helper function to render status icon dynamically
  const renderStatusIcon = (status) => {
    const iconComponentName = statusManager.getStatusIconComponent(status);
    
    // Map icon component names to actual components
    const iconMap = {
      'FeatherFileText': <FeatherFileText />,
      'FeatherUsers': <FeatherUsers />,
      'FeatherAlertCircle': <FeatherAlertCircle />,
      'FeatherClock': <FeatherClock />,
      'FeatherActivity': <FeatherActivity />,
      'FeatherFlag': <FeatherFlag />,
      'FeatherCheckCircle': <FeatherCheckCircle />,
      'FeatherRefreshCw': <FeatherRefreshCw />,
      'FeatherX': <FeatherX />,
      'FeatherArrowLeft': <FeatherArrowLeft />,
      'FeatherCalendar': <FeatherCalendar />,
      'FeatherTrash2': <FeatherTrash2 />
    };
    
    return iconMap[iconComponentName] || <FeatherFileText />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <FeatherFileText /> },
    { id: 'contact-history', label: 'Contact History', icon: <FeatherPhone /> },
    { id: 'communication', label: 'Communication', icon: <FeatherMessageSquare /> },
    { id: 'activity', label: 'Activity Feed', icon: <FeatherActivity /> },
    { id: 'reminders', label: 'Reminders', icon: <FeatherBell /> }
  ];

  if (loading) {
    return (
      <div className="company-detail-modal">
        <div className="modal-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading company details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-detail-modal">
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <div className="header-left">
            <h2>{companyData?.name}</h2>
            {companyData?.industry && (
              <Badge variant="neutral-tertiary">{companyData.industry}</Badge>
            )}
          </div>
          <div className="header-actions">
            <IconButton
              variant="neutral-tertiary"
              icon={<FeatherEdit2 />}
              onClick={() => onEdit(companyData)}
              title="Edit Company"
            />
            <IconButton
              variant="neutral-tertiary"
              icon={<FeatherX />}
              onClick={onClose}
              title="Close"
            />
          </div>
        </div>

        {/* Status Bar */}
        <div className="status-bar">
          <div className="status-info">
            {renderStatusIcon(companyData?.partner_status?.current_status || 'initial_data_entered')}
            <Badge variant={statusManager.getStatusColor(companyData?.partner_status?.current_status || 'initial_data_entered')}>
              {formatStatus(companyData?.partner_status?.current_status || 'initial_data_entered')}
            </Badge>
          </div>
          {/* <select
            value={companyData?.partner_status?.current_status || 'initial_data_entered'}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="status-select"
            disabled={
              companyData?.partner_status?.current_status === 'contacted' ||
              companyData?.partner_status?.current_status === 'fully_onboarded' ||
              companyData?.partner_status?.current_status === 'agreement_signed' || 
              companyData?.partner_status?.current_status === 'rejected_not_interested' ||
              companyData?.partner_status?.current_status === 'not_eligible' ||
              companyData?.partner_status?.current_status === 'not_interested'
            }
            title={
              companyData?.partner_status?.current_status === 'contacted'
                ? 'Use Quick Log to update status based on contact outcome'
                : companyData?.partner_status?.current_status === 'fully_onboarded' || companyData?.partner_status?.current_status === 'agreement_signed' || companyData?.partner_status?.current_status === 'rejected_not_interested' || companyData?.partner_status?.current_status === 'not_eligible' || companyData?.partner_status?.current_status === 'not_interested'
                ? 'Final status - cannot be changed'
                : 'Change company status'
            }
          >
            {getAvailableStatusOptions(companyData?.partner_status?.current_status || 'initial_data_entered').map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select> */}
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <Button
              variant="neutral-tertiary"
              size="sm"
              onClick={() => setError('')}
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="overview-grid">
                {/* Section 1: Company Profile */}
                <div className="info-section">
                  <h3>Company Profile</h3>
                  <div className="info-grid">
                    {companyData?.website && (
                      <div className="info-item">
                        <FeatherGlobe size={16} />
                        <span className="label">Website:</span>
                        <a 
                          href={companyData.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="value link"
                        >
                          {companyData.website}
                        </a>
                      </div>
                    )}
                    
                    {companyData?.email && (
                      <div className="info-item">
                        <FeatherMail size={16} />
                        <span className="label">Company Email:</span>
                        <a 
                          href={`mailto:${companyData.email}`}
                          className="value link"
                        >
                          {companyData.email}
                        </a>
                      </div>
                    )}
                    
                    {companyData?.phone && (
                      <div className="info-item">
                        <FeatherPhone size={16} />
                        <span className="label">Company Phone:</span>
                        <a 
                          href={`tel:${companyData.phone}`}
                          className="value link"
                        >
                          {companyData.phone}
                        </a>
                      </div>
                    )}
                    
                    {companyData?.location && (
                      <div className="info-item">
                        <FeatherGlobe size={16} />
                        <span className="label">Location:</span>
                        <span className="value">{companyData.location}</span>
                      </div>
                    )}
                    
                    {companyData?.years_in_business && (
                      <div className="info-item">
                        <FeatherCalendar size={16} />
                        <span className="label">Years in Business:</span>
                        <span className="value">{companyData.years_in_business} years</span>
                      </div>
                    )}
                    
                    {companyData?.company_size && (
                      <div className="info-item">
                        <FeatherUsers size={16} />
                        <span className="label">Company Size:</span>
                        <span className="value">{companyData.company_size}</span>
                      </div>
                    )}
                    
                    {companyData?.timezone && (
                      <div className="info-item">
                        <FeatherClock size={16} />
                        <span className="label">Timezone:</span>
                        <span className="value">{companyData.timezone}</span>
                      </div>
                    )}
                    
                    {companyData?.working_hours && (
                      <div className="info-item">
                        <FeatherClock size={16} />
                        <span className="label">Working Hours:</span>
                        <span className="value">{companyData.working_hours}</span>
                      </div>
                    )}
                    
                    {companyData?.assigned_to_user && (
                      <div className="info-item">
                        <FeatherUser size={16} />
                        <span className="label">Assigned To:</span>
                        <span className="value">
                          {companyData.assigned_to_user.display_name || companyData.assigned_to_user.email}
                        </span>
                      </div>
                    )}
                    
                    {companyData?.willing_to_partner !== undefined && (
                      <div className="info-item">
                        <FeatherCheckCircle size={16} />
                        <span className="label">Willing to Work:</span>
                        <span className="value">
                          {companyData.willing_to_partner ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                    

                  </div>
                  
                  {/* Language Proficiency */}
                  {companyData?.language_proficiency?.length > 0 && (
                    <div className="capability-group">
                      <h4>Language Proficiency</h4>
                      <div className="tags">
                        {companyData.language_proficiency.map((lang, index) => (
                          <Badge key={index} variant="neutral-tertiary">{lang}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Certifications */}
                  {companyData?.certifications?.length > 0 && (
                    <div className="capability-group">
                      <h4>Certifications</h4>
                      <div className="tags">
                        {companyData.certifications.map((cert, index) => (
                          <Badge key={index} variant="info">{cert}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Section 2: Key Contacts */}
                <div className="info-section">
                  <h3>Key Contacts</h3>
                  
                  {/* Primary Contact */}
                  {(companyData?.primary_contact?.name || companyData?.primary_contact?.email || companyData?.primary_contact?.phone) && (
                    <div className="contact-group">
                      <h4>Primary Contact</h4>
                      <div className="contact-person">
                        {companyData.primary_contact.name && (
                          <div className="person-header">
                            <FeatherUser size={16} />
                            <span className="person-name">{companyData.primary_contact.name}</span>
                            {companyData.primary_contact.role && (
                              <Badge variant="neutral-tertiary" size="sm">{companyData.primary_contact.role}</Badge>
                            )}
                          </div>
                        )}
                        {companyData.primary_contact.email && (
                          <a href={`mailto:${companyData.primary_contact.email}`} className="person-email">
                            {companyData.primary_contact.email}
                          </a>
                        )}
                        {companyData.primary_contact.phone && (
                          <a href={`tel:${companyData.primary_contact.phone}`} className="person-phone">
                            {companyData.primary_contact.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Decision Maker */}
                  {(companyData?.decision_maker?.name || companyData?.decision_maker?.email) && (
                    <div className="contact-group">
                      <h4>Decision Maker</h4>
                      <div className="contact-person">
                        {companyData.decision_maker.name && (
                          <div className="person-header">
                            <FeatherUser size={16} />
                            <span className="person-name">{companyData.decision_maker.name}</span>
                            {companyData.decision_maker.role && (
                              <Badge variant="neutral-tertiary" size="sm">{companyData.decision_maker.role}</Badge>
                            )}
                          </div>
                        )}
                        {companyData.decision_maker.email && (
                          <a href={`mailto:${companyData.decision_maker.email}`} className="person-email">
                            {companyData.decision_maker.email}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Communication Preferences */}
                  {(companyData?.communication_style || companyData?.availability_for_calls) && (
                    <div className="communication-preferences">
                      <h4>Communication Preferences</h4>
                      <div className="info-grid">
                        {companyData?.communication_style && (
                          <div className="info-item">
                            <span className="label">Style:</span>
                            <span className="value">{companyData.communication_style}</span>
                          </div>
                        )}
                        {companyData?.availability_for_calls && (
                          <div className="info-item">
                            <span className="label">Availability:</span>
                            <span className="value">{companyData.availability_for_calls}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Section 3: Technical Capabilities */}
                {(companyData?.technology_stack?.length > 0 || companyData?.skill_categories?.length > 0 || companyData?.specializations?.length > 0) && (
                  <div className="info-section">
                    <h3>Technical Capabilities</h3>
                    
                    {companyData?.technology_stack?.length > 0 && (
                      <div className="capability-group">
                        <h4>Technology Stack</h4>
                        <div className="tags">
                          {companyData.technology_stack.map((tech, index) => (
                            <Badge key={index} variant="neutral-tertiary">{tech}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {companyData?.skill_categories?.length > 0 && (
                      <div className="capability-group">
                        <h4>Skill Categories</h4>
                        <div className="tags">
                          {companyData.skill_categories.map((category, index) => (
                            <Badge key={index} variant="info">{category}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {companyData?.specializations?.length > 0 && (
                      <div className="capability-group">
                        <h4>Specializations</h4>
                        <div className="tags">
                          {companyData.specializations.map((spec, index) => (
                            <Badge key={index} variant="warning">{spec}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {companyData?.delivery_model?.length > 0 && (
                      <div className="capability-group">
                        <h4>Delivery Model</h4>
                        <div className="tags">
                          {companyData.delivery_model.map((model, index) => (
                            <Badge key={index} variant="success">{model}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Section 4: Resource Availability */}
                {(companyData?.team_size || companyData?.current_capacity || companyData?.scalability || companyData?.response_time) && (
                  <div className="info-section">
                    <h3>Resource Availability</h3>
                    <div className="info-grid">
                      {companyData?.team_size && (
                        <div className="info-item">
                          <FeatherUsers size={16} />
                          <span className="label">Team Size:</span>
                          <span className="value">{companyData.team_size} people</span>
                        </div>
                      )}
                      
                      {companyData?.current_capacity && (
                        <div className="info-item">
                          <FeatherActivity size={16} />
                          <span className="label">Current Capacity:</span>
                          <span className="value">{companyData.current_capacity.replace('_', ' ')}</span>
                        </div>
                      )}
                      
                      {companyData?.scalability && (
                        <div className="info-item">
                          <FeatherTrendingUp size={16} />
                          <span className="label">Scalability:</span>
                          <span className="value">{companyData.scalability}</span>
                        </div>
                      )}
                      
                      {companyData?.response_time && (
                        <div className="info-item">
                          <FeatherClock size={16} />
                          <span className="label">Response Time:</span>
                          <span className="value">{companyData.response_time.replace('_', ' ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Section 5: Project Delivery Capability */}
                {(companyData?.project_types?.length > 0 || companyData?.size_range?.length > 0 || companyData?.methodology?.length > 0) && (
                  <div className="info-section">
                    <h3>Project Delivery Capability</h3>
                    
                    {companyData?.project_types?.length > 0 && (
                      <div className="capability-group">
                        <h4>Project Types</h4>
                        <div className="tags">
                          {companyData.project_types.map((type, index) => (
                            <Badge key={index} variant="neutral-tertiary">{type}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {companyData?.size_range?.length > 0 && (
                      <div className="capability-group">
                        <h4>Size Range</h4>
                        <div className="tags">
                          {companyData.size_range.map((size, index) => (
                            <Badge key={index} variant="info">{size}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {companyData?.methodology?.length > 0 && (
                      <div className="capability-group">
                        <h4>Methodology</h4>
                        <div className="tags">
                          {companyData.methodology.map((method, index) => (
                            <Badge key={index} variant="warning">{method}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {companyData?.qa_processes?.length > 0 && (
                      <div className="capability-group">
                        <h4>QA Processes</h4>
                        <div className="tags">
                          {companyData.qa_processes.map((process, index) => (
                            <Badge key={index} variant="success">{process}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {(companyData?.minimum_engagement || companyData?.travel_policy) && (
                      <div className="engagement-details">
                        <h4>Engagement Details</h4>
                        <div className="info-grid">
                          {companyData?.minimum_engagement && (
                            <div className="info-item">
                              <span className="label">Minimum Engagement:</span>
                              <span className="value">{companyData.minimum_engagement}</span>
                            </div>
                          )}
                          {companyData?.travel_policy && (
                            <div className="info-item">
                              <span className="label">Travel Policy:</span>
                              <span className="value">{companyData.travel_policy}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Section 6: Financial Terms */}
                {(companyData?.rate_ranges || companyData?.contract_preferences?.length > 0 || companyData?.payment_terms || companyData?.currency) && (
                  <div className="info-section">
                    <h3>Financial Terms</h3>
                    
                    {companyData?.rate_ranges && Object.keys(companyData.rate_ranges).length > 0 && (
                      <div className="capability-group">
                        <h4>Rate Ranges</h4>
                        <div className="rate-ranges">
                          {Object.entries(companyData.rate_ranges).map(([level, rates]) => (
                            <div key={level} className="rate-level">
                              <span className="level-name">{level}:</span>
                              <span className="rate-range">
                                ${rates.min} - ${rates.max}/hr
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {companyData?.contract_preferences?.length > 0 && (
                      <div className="capability-group">
                        <h4>Contract Preferences</h4>
                        <div className="tags">
                          {companyData.contract_preferences.map((pref, index) => (
                            <Badge key={index} variant="neutral-tertiary">{pref}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="info-grid">
                      {companyData?.payment_terms && (
                        <div className="info-item">
                          <span className="label">Payment Terms:</span>
                          <span className="value">{companyData.payment_terms}</span>
                        </div>
                      )}
                      
                      {companyData?.invoicing_format && (
                        <div className="info-item">
                          <span className="label">Invoicing Format:</span>
                          <span className="value">{companyData.invoicing_format}</span>
                        </div>
                      )}
                      
                      {companyData?.currency && (
                        <div className="info-item">
                          <span className="label">Currency:</span>
                          <span className="value">{companyData.currency}</span>
                        </div>
                      )}
                      
                      {companyData?.tax_id && (
                        <div className="info-item">
                          <span className="label">Tax ID:</span>
                          <span className="value">{companyData.tax_id}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Documents */}
                <div className="info-section">
                  <h3>Documents</h3>
                  <div className="documents-grid">
                    <div className="document-item">
                      <FeatherFileText size={16} />
                      <span className="label">Rate Card:</span>
                      {companyData?.rate_card_url ? (
                        <a 
                          href={companyData.rate_card_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="value link"
                        >
                          View Rate Card
                        </a>
                      ) : (
                        <span className="value">Not uploaded</span>
                      )}
                    </div>
                    
                    <div className="document-item">
                      <FeatherFileText size={16} />
                      <span className="label">Agreement:</span>
                      {companyData?.agreement_file_url ? (
                        <a 
                          href={companyData.agreement_file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="value link"
                        >
                          View Agreement
                        </a>
                      ) : (
                        <span className="value">Not uploaded</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {companyData?.partner_tag_relations?.length > 0 && (
                  <div className="info-section">
                    <h3>Tags</h3>
                    <div className="tags-list">
                      {companyData.partner_tag_relations.map(relation => (
                        <Badge 
                          key={relation.partner_tags.id}
                          variant="neutral-tertiary"
                          style={{ 
                            backgroundColor: relation.partner_tags.color + '20', 
                            color: relation.partner_tags.color 
                          }}
                        >
                          <FeatherTag size={12} />
                          {relation.partner_tags.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {companyData?.initial_notes && (
                  <div className="info-section">
                    <h3>Notes</h3>
                    <div className="notes-content">
                      {companyData.initial_notes}
                    </div>
                  </div>
                )}


              </div>
            </div>
          )}

          {activeTab === 'contact-history' && (
            <ContactHistory 
              partner={companyData}
              onUpdate={fetchCompanyDetails}
            />
          )}

          {activeTab === 'communication' && (
            <CommunicationLogs 
              companyId={companyData?.id}
              onRefresh={fetchCompanyDetails}
            />
          )}

          {activeTab === 'activity' && (
            <ActivityFeed 
              companyId={companyData?.id}
            />
          )}

          {activeTab === 'reminders' && (
            <Reminders 
              companyId={companyData?.id}
              users={users}
              onRefresh={fetchCompanyDetails}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailView; 